import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Export data to Excel file
 * @param {Array} data - Array of objects to export
 * @param {String} fileName - Name of the file (without extension)
 * @param {Array} columns - Optional array of column definitions { header, key, width }
 */
export const exportToExcel = (data, fileName = 'export', columns = null) => {
  try {
    // If columns are provided, map data to match column keys
    let exportData = data;
    if (columns && columns.length > 0) {
      exportData = data.map(row => {
        const mappedRow = {};
        columns.forEach(col => {
          const value = getNestedValue(row, col.key);
          mappedRow[col.header] = formatValue(value);
        });
        return mappedRow;
      });
    }

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths if provided
    if (columns && columns.length > 0) {
      const colWidths = columns.map(col => ({
        wch: col.width || 15
      }));
      ws['!cols'] = colWidths;
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const fullFileName = `${fileName}_${timestamp}.xlsx`;

    // Save file
    XLSX.writeFile(wb, fullFileName);
    return true;
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw new Error('Failed to export to Excel');
  }
};

/**
 * Export data to PDF file
 * @param {Array} data - Array of objects to export
 * @param {String} fileName - Name of the file (without extension)
 * @param {Array} columns - Array of column definitions { header, key, width }
 * @param {Object} options - Additional options { title, orientation }
 */
export const exportToPDF = (data, fileName = 'export', columns, options = {}) => {
  try {
    console.log('PDF Export Debug:', { data, fileName, columns, options });
    
    // Validate inputs
    if (!data || !Array.isArray(data)) {
      throw new Error('Data must be an array');
    }
    
    if (!columns || !Array.isArray(columns) || columns.length === 0) {
      throw new Error('Columns must be provided as an array');
    }
    
    if (data.length === 0) {
      throw new Error('No data to export');
    }

    const {
      title = 'Data Export',
      orientation = 'landscape',
      includeTimestamp = true,
      fontSize = 8
    } = options;

    console.log('Creating PDF with options:', { title, orientation, includeTimestamp, fontSize });

    // Create PDF document
    const doc = new jsPDF({
      orientation: orientation,
      unit: 'mm',
      format: 'a4'
    });

    console.log('PDF document created successfully');

    // Add title
    doc.setFontSize(16);
    doc.text(title, 14, 15);

    // Add timestamp if requested
    if (includeTimestamp) {
      doc.setFontSize(10);
      const timestamp = new Date().toLocaleString();
      doc.text(`Generated: ${timestamp}`, 14, 22);
    }

    console.log('Title and timestamp added');

    // Prepare table data
    const headers = columns.map(col => col.header);
    console.log('Headers prepared:', headers);
    
    const rows = data.map((row, index) => {
      try {
        return columns.map(col => {
          const value = getNestedValue(row, col.key);
          return formatValue(value);
        });
      } catch (error) {
        console.error(`Error processing row ${index}:`, error, row);
        return columns.map(() => 'Error');
      }
    });
    
    console.log('Rows prepared:', rows.length, 'rows');

    // Add table using the imported autoTable function
    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: includeTimestamp ? 25 : 20,
      styles: {
        fontSize: fontSize,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { top: 25 }
    });

    console.log('Table added to PDF');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const fullFileName = `${fileName}_${timestamp}.pdf`;

    console.log('Saving PDF as:', fullFileName);

    // Save PDF
    doc.save(fullFileName);
    console.log('PDF saved successfully');
    return true;
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    console.error('Error stack:', error.stack);
    throw new Error(`Failed to export to PDF: ${error.message}`);
  }
};

/**
 * Format value for export
 * @param {*} value - The value to format
 */
const formatValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    // For objects, try to get a meaningful string representation
    if (value.clientName) return value.clientName;
    if (value.firstName && value.lastName) return `${value.firstName} ${value.lastName}`;
    if (value.name) return value.name;
    if (value.email) return value.email;
    return JSON.stringify(value);
  }
  
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  
  return String(value);
};

/**
 * Get nested value from object using dot notation
 * @param {Object} obj - The object to extract value from
 * @param {String} path - Dot notation path (e.g., 'user.name')
 */
const getNestedValue = (obj, path) => {
  if (!path) return '';
  
  return path.split('.').reduce((current, key) => {
    // Handle array notation like "items[0].name"
    if (key.includes('[')) {
      const arrayMatch = key.match(/^(.+)\[(\d+)\]$/);
      if (arrayMatch) {
        const [, arrayKey, index] = arrayMatch;
        return current?.[arrayKey]?.[parseInt(index)];
      }
    }
    return current?.[key];
  }, obj);
};

/**
 * Create export handlers for components
 * @param {Array} data - Data to export
 * @param {String} fileName - Base filename
 * @param {Array} columns - Column definitions
 * @param {Object} pdfOptions - PDF-specific options
 */
export const createExportHandlers = (data, fileName, columns, pdfOptions = {}) => {
  return {
    handleExportExcel: () => {
      try {
        exportToExcel(data, fileName, columns);
      } catch (error) {
        console.error('Export to Excel failed:', error);
        alert('Failed to export to Excel');
      }
    },
    handleExportPDF: () => {
      try {
        exportToPDF(data, fileName, columns, pdfOptions);
      } catch (error) {
        console.error('Export to PDF failed:', error);
        alert('Failed to export to PDF');
      }
    }
  };
};

export default {
  exportToExcel,
  exportToPDF,
  createExportHandlers
};